<template>
  <v-row class="no-gutters">
    <v-col class="col-12">
      <v-card flat>
        <v-card-text class="px-0">
          <v-row class="no-gutters">
            <!-- search field -->
            <v-col :class="[captureUser ? 'col-7' : 'col-6']">
              <search-user-autocomplete
                ref="searchUserAutocomplete"
                :is-loading.sync="isLoading"
                :users-instance="usersInstance"
                :selected-user-bookings="selectedUserBookings"
                :required="selectedUserBookingsRequired"
                :searchForBookings="bookingMode !== 'hidden'"
                @update:selectedUserBookings="
                  (userBookings) => {
                    this.$emit('update:selectedUserBookings', userBookings)
                  }
                "
                @lastStringSearched="
                  (lastStringSearched) => {
                    this.$emit('lastStringSearched', lastStringSearched)
                  }
                "
              />
            </v-col>

            <!-- capture new user -->
            <v-col v-if="captureUser" class="col-5 text-right mt-2">
              <v-btn
                color="primary"
                large
                @click="$refs.addNewShadowUser.show()"
              >
                <span class="fal fa-user-plus mr-2" />
                {{ $t('personalData.addNewPerson') }}
              </v-btn>
            </v-col>

            <!-- create new booking -->
            <v-col v-if="createBooking" class="col-6 text-right mt-2">
              <enter-booking-btn />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- show bookings -->
    <v-col v-if="showBookings" class="col-12 pt-0">
      <v-card flat>
        <v-card-title>
          <v-row>
            <v-col class="col-8 py-0">
              {{ $t('shopBackendBookingOverview.bookingsOfTheUser') }}
            </v-col>

            <!-- hide bookings -->
            <v-col
              v-if="bookingMode === 'optional'"
              class="col-4 clickable hover-underline text-right content-3 grey--text text--darken-2 py-0"
              @click="showBookings = false"
            >
              <span class="fal fa-eye-slash mr-2" />
              {{ $t('bookingByCustomerSearch.hideBookings') }}
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="selectedBookings"
            :items-per-page="20"
            :no-data-text="$t('shopBackendBookingOverview.noSearchResult')"
            :no-results-text="$t('dataTable.noResultFound')"
            :footer-props="{ itemsPerPageText: $t('general.rowsPerPage') }"
            :loading="isLoading"
            :loading-text="$t('general.loadingData')"
            @click:row="rowSelected"
          >
            <template #item="props">
              <tr class="clickable" @click="rowSelected(props.item)">
                <!-- date -->
                <td>
                  {{ props.item.getBookingBoundaryDates() }}
                </td>

                <!-- customer -->
                <td>
                  <display-user-helper :user="selectedUserBookings" />
                </td>

                <!-- tickets count -->
                <td>
                  <v-row>
                    <v-col class="text-center">
                      {{ props.item.getBookingEntries().length }}
                    </v-col>
                  </v-row>
                </td>

                <!-- remark -->
                <td>
                  {{ props.item.getNote() }}
                </td>

                <!-- booking state -->
                <td>
                  <span
                    v-if="props.item.getBookingStates().isConfirmed()"
                    class="fal fa-check green--text mr-1"
                  />
                  <span v-else class="fal fa-clock primary--text mr-1" />

                  {{ props.item.getBookingStates().getStateText() }}
                </td>

                <!-- paid? -->
                <td>
                  <!-- yes, it's paid -->
                  <span v-if="props.item.isPaid()">
                    <span class="fal fa-check mr-1 green--text" />
                    {{ $t('shopBackendBookingDetail.paid') }}
                  </span>

                  <!-- unpaid booking -->
                  <span v-else>
                    <span class="fal fa-exclamation-triangle mr-1 red--text" />
                    {{ $t('bookingByCustomerSearch.unpaid') }}
                  </span>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- display user's bookings -->
    <v-col
      v-else-if="selectedBookings.length && bookingMode !== 'hidden'"
      class="col-12 ml-4 clickable hover-underline content-3 grey--text text--darken-2"
      @click="showBookings = true"
    >
      <span class="fal fa-eye mr-2" />
      {{
        $tc(
          'bookingByCustomerSearch.userHasBookings',
          selectedBookings.length,
          {
            user: selectedUserBookings.getFullName(),
            bookings: selectedBookings.length,
          }
        )
      }}
    </v-col>

    <!-- capture new main user -->
    <add-new-profile
      ref="addNewShadowUser"
      hide-card-list-switch
      :show-card-list="false"
      :additionalFields="{
        address: '',
        city: '',
        zip: '',
        country: '',
        mail: '',
        phone: '',
        company: '',
        salutations: salutations,
      }"
      :mandatory-fields="mandatoryFields"
      :shadow="false"
      :foreignUidForShadowUser="foreignUidForShadowUser"
      reset-fields
      @refresh="setNewSelectedUserBookings"
    />
  </v-row>
</template>

<script>
import Users from '../../classes-shared/user/Users'
import SearchUserAutocomplete from './SearchUserAutocomplete'
import AddNewProfile from '../../components-shared/users/AddNewProfile'
import UserBookings from '../../classes-shared/bookings/UserBookings'
import EnterBookingBtn from './EnterBookingBtn'
import Salutations from '../../classes-shared/salutations/Salutations'
import DisplayUserHelper from './DisplayUserHelper'

export default {
  name: 'BookingByCustomerSearch',
  components: {
    DisplayUserHelper,
    EnterBookingBtn,
    SearchUserAutocomplete,
    AddNewProfile,
  },
  props: {
    // how to display the bookings of an user
    // type: optional | hidden
    bookingMode: {
      type: String,
      required: false,
      default: null,
    },

    // button to capture a new user
    captureUser: {
      type: Boolean,
      required: false,
      default: false,
    },

    // button to create new booking
    createBooking: {
      type: Boolean,
      required: false,
      default: false,
    },

    // sets the selectedUserBookings in SearchUserAutocomplete.vue mandatory via rules
    selectedUserBookingsRequired: {
      type: Boolean,
      required: false,
      default: false,
    },

    // the selected user with its bookings and shadow users
    selectedUserBookings: {
      type: UserBookings,
      default: null,
      required: true,
    },
  },

  data() {
    return {
      // new autocomplete
      model: null,
      items: null,
      isLoading: false,
      usersInstance: new Users(),
      salutations: new Salutations(),
      headers: [
        {
          text: this.$t('shopBackendBookingOverview.validity'),
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingOverview.customer'),
          sortable: false,
        },
        {
          text: this.$t('shopBackendBookingOverview.ticketCount'),
          sortable: false,
          align: 'center',
        },
        {
          text: this.$t('shopBackendBookingComplete.remark'),
          sortable: false,
          align: 'left',
        },
        {
          text: this.$t('shopBackendBookingOverview.bookingState'),
          align: 'left',
          sortable: false,
          width: 221,
        },
        {
          text: this.$t('shopBackendBookingDetail.paymentState'),
          align: 'left',
          sortable: false,
          width: 125,
        },
      ],

      showBookings: true,

      // AddNewProfile.vue props
      showCardList: false,
    }
  },

  computed: {
    // short cut
    selectedBookings() {
      if (this.selectedUserBookings)
        return this.selectedUserBookings.userBookings
      return []
    },
    foreignUidForShadowUser() {
      return this.$store.getters.getAppUserInstance()?.getUid()
    },
    mandatoryFields() {
      if (store.getters.getCurrentDestinationInstance().getSlug() === 'niesen')
        return [
          'lastName',
          'firstName',
          'mail',
          'salutations',
          'country',
          'phone',
        ]

      return ['lastName', 'firstName', 'mail', 'salutations', 'country']
    },
  },

  beforeMount() {
    if (this.bookingMode === 'optional' || this.bookingMode === 'hidden')
      this.showBookings = false

    EventBus.$on('bookingByCustomerSearch:addToUserList', (userBookings) => {
      this.usersInstance.addUserBooking(userBookings)
    })
  },

  mounted() {
    this.$refs.searchUserAutocomplete.focus()
  },

  beforeDestroy() {
    EventBus.$off('bookingByCustomerSearch:addToUserList')
  },

  methods: {
    rowSelected(item) {
      this.$router.push({ name: 'bookingDetailView', params: { id: item.id } })
    },

    // if a new user was created
    setNewSelectedUserBookings(rawUserData) {
      this.$emit('update:selectedUserBookings', new UserBookings(rawUserData))
      this.usersInstance.addUserBooking(new UserBookings(rawUserData))
    },
  },
}
</script>

<style scoped>
div {
  cursor: pointer !important;
}
</style>
